import clsx from 'clsx';
import { PersonFill } from 'react-bootstrap-icons';

import { SDSAvatar } from '@repo/sds/SDSAvatar';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSText } from '@repo/sds/SDSText';
import { FileInputWithDragAndDrop } from '@repo/ui/components/fileInputs/FileInputWithDragAndDrop/FileInputWithDragAndDrop';
import { ActionIconWrapper } from '@repo/ui/components/iconWrappers/ActionIconWrapper/ActionIconWrapper';

export interface SharedFileInputProps {
  accept: string;
  onChange: (file: File) => void | Promise<void>;
  onError: (message: string) => void;
  maxFileSizeBytes: number;
  isDisabled: boolean;
}

interface ImageInputProfileViewProps {
  src: string | undefined;
  previewAlt: string;
  previewDataTestId?: string;
  displayName: string;
  displayNameDataTestId?: string;
  profileContentDataTestId?: string;
  dataTestId?: string;
  formFieldName?: string;
  fileInputProps: SharedFileInputProps;
  canRemove: boolean;
  isDisabled: boolean;
  removeDataTestId?: string;
  onDelete: () => void;
}

function ImageInputProfileView({
  src,
  previewAlt,
  previewDataTestId,
  displayName,
  displayNameDataTestId,
  profileContentDataTestId,
  dataTestId,
  formFieldName,
  fileInputProps,
  canRemove,
  isDisabled,
  removeDataTestId,
  onDelete,
}: ImageInputProfileViewProps) {
  return (
    <SDSFlexbox direction="column" alignItems="center" gap="text-large">
      <FileInputWithDragAndDrop
        {...fileInputProps}
        inputAriaLabel={src ? 'Change profile picture' : 'Upload profile picture'}
        dataTestId={dataTestId}
        formFieldName={formFieldName}
        className="ImageInput__profile-card"
      >
        <SDSFlexbox
          direction="column"
          justifyContent="center"
          alignItems="center"
          className="ImageInput__profile-content"
          dataTestId={profileContentDataTestId}
        >
          <div className="ImageInput__profile-gradient" />
          <SDSFlexbox direction="column" gap="no-design-16" alignItems="center" fullWidth>
            {src ? (
              <SDSAvatar
                src={src}
                alt={previewAlt}
                size="xLarge"
                className="ImageInput__profile-avatar"
                dataTestId={previewDataTestId}
              />
            ) : (
              <div className="ImageInput__profile-placeholder">
                <SDSIcon icon={<PersonFill />} className="ImageInput__profile-placeholder-icon" />
              </div>
            )}
            <SDSText
              type="body-medium"
              text={src ? 'Change Picture' : displayName}
              color="tertiary"
              dataTestId={displayNameDataTestId}
            />
          </SDSFlexbox>
        </SDSFlexbox>
      </FileInputWithDragAndDrop>
      {src && canRemove ? (
        <SDSButton
          mode="outline"
          text="Remove"
          onClick={onDelete}
          ariaLabel="Remove profile picture"
          dataTestId={removeDataTestId}
          isDisabled={isDisabled}
        />
      ) : null}
    </SDSFlexbox>
  );
}

interface ImageInputPreviewProps {
  src: string;
  previewAlt: string;
  previewDataTestId?: string;
  canEdit: boolean;
  editDataTestId?: string;
  isDisabled: boolean;
  onEdit: () => void;
  fileInputProps: SharedFileInputProps;
  dataTestId?: string;
  replaceDataTestId?: string;
  formFieldName?: string;
  canRemove: boolean;
  removeDataTestId?: string;
  onDelete: () => void;
}

function ImageInputPreview({
  src,
  previewAlt,
  previewDataTestId,
  canEdit,
  editDataTestId,
  isDisabled,
  onEdit,
  fileInputProps,
  dataTestId,
  replaceDataTestId,
  formFieldName,
  canRemove,
  removeDataTestId,
  onDelete,
}: ImageInputPreviewProps) {
  return (
    <div className="ImageInput__container">
      <div className="ImageInput__image-wrapper">
        <img src={src} alt={previewAlt} className="ImageInput__image" data-testid={previewDataTestId} />
        <div className="ImageInput__image-action-section">
          {canEdit ? (
            <SDSPrimitiveButton
              onClick={onEdit}
              ariaLabel="Edit image"
              dataTestId={editDataTestId}
              isDisabled={isDisabled}
            >
              <div className="ImageInput__image-action-trigger">
                <ActionIconWrapper action="edit" className="ImageInput__image-action-item" />
              </div>
            </SDSPrimitiveButton>
          ) : null}
          <FileInputWithDragAndDrop
            {...fileInputProps}
            inputAriaLabel="Replace image"
            dataTestId={replaceDataTestId ?? dataTestId}
            formFieldName={formFieldName}
          >
            <div className="ImageInput__image-action-trigger">
              <ActionIconWrapper action="add" className="ImageInput__image-action-item" />
            </div>
          </FileInputWithDragAndDrop>
          {canRemove ? (
            <SDSPrimitiveButton
              onClick={onDelete}
              ariaLabel="Delete image"
              dataTestId={removeDataTestId}
              isDisabled={isDisabled}
            >
              <ActionIconWrapper action="delete" className="ImageInput__image-action-item" />
            </SDSPrimitiveButton>
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface ImageInputEmptyStateProps {
  fileInputProps: SharedFileInputProps;
  dataTestId?: string;
  formFieldName?: string;
  displayName: string;
  displayNameDataTestId?: string;
}

interface ImageInputBodyProps {
  presentation: 'default' | 'profile';
  src: string | undefined;
  previewAlt: string;
  previewDataTestId?: string;
  displayName: string;
  displayNameDataTestId?: string;
  profileContentDataTestId?: string;
  dataTestId?: string;
  replaceDataTestId?: string;
  formFieldName?: string;
  fileInputProps: SharedFileInputProps;
  canRemove: boolean;
  canEdit: boolean;
  isDisabled: boolean;
  editDataTestId?: string;
  removeDataTestId?: string;
  onEdit: () => void;
  onDelete: () => void;
}

export function ImageInputBody({ presentation, src, ...viewProps }: ImageInputBodyProps) {
  if (presentation === 'profile') {
    return <ImageInputProfileView src={src} {...viewProps} />;
  }

  if (src) {
    return <ImageInputPreview src={src} {...viewProps} />;
  }

  return <ImageInputEmptyState {...viewProps} />;
}

function ImageInputEmptyState({
  fileInputProps,
  dataTestId,
  formFieldName,
  displayName,
  displayNameDataTestId,
}: ImageInputEmptyStateProps) {
  return (
    <FileInputWithDragAndDrop
      {...fileInputProps}
      inputAriaLabel="Upload an image"
      dataTestId={dataTestId}
      formFieldName={formFieldName}
      className="ImageInput ImageInput__container"
    >
      <div className={clsx('AddItemCard', 'ImageInput__add-item')}>
        <ActionIconWrapper action="add" className="AddItemCard__icon" />
        <SDSText
          type="button-medium"
          className="AddItemCard__text"
          text={displayName}
          dataTestId={displayNameDataTestId}
        />
      </div>
    </FileInputWithDragAndDrop>
  );
}
